import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { UserContext } from '../context/UserContext';
import { AdminUserRecord, deleteAdminUser, getAdminUsers, sendOnboardingReminder } from '../services/admin';

const AdminUsers: React.FC = () => {
  const { user, loading: userContextLoading } = useContext(UserContext);
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [sendingReminderEmail, setSendingReminderEmail] = useState<string | null>(null);
  const [endingWithinDays, setEndingWithinDays] = useState(30);
  const [onboardingFilter, setOnboardingFilter] = useState<string>('all');
  const [summary, setSummary] = useState<{
    endingWithinDays: number;
    endingSoonCount: number;
    status: Record<string, number>;
  } | null>(null);

  const isAdmin = user?.role === 'admin';
  const pageBackground = { background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' };

  const fetchUsers = async () => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getAdminUsers({
        page: 1,
        limit: 100,
        endingWithinDays,
        onboardingStatus: onboardingFilter === 'all' ? undefined : onboardingFilter
      });
      setUsers(response.users || []);
      setSummary(response.summary || null);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Unable to load admin users right now.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [isAdmin, endingWithinDays, onboardingFilter]);

  const handleDeleteUser = async (record: AdminUserRecord) => {
    const agreed = window.confirm(`Delete user ${record.email}? This cannot be undone.`);
    if (!agreed) return;

    try {
      setDeletingUserId(record.id);
      setActionMessage(null);
      await deleteAdminUser(record.id);
      setActionMessage(`Deleted ${record.email}.`);
      await fetchUsers();
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to delete user.';
      setActionMessage(message);
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleSendReminder = async (email: string) => {
    try {
      setSendingReminderEmail(email);
      setActionMessage(null);
      const res = await sendOnboardingReminder(email);
      setActionMessage(`Onboarding reminder sent to ${email}.`);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to send onboarding reminder.';
      setActionMessage(message);
    } finally {
      setSendingReminderEmail(null);
    }
  };

  const pendingCount = users.filter((u) => u.onboardingStatus === 'password_pending').length;
  const completedCount = users.filter((u) => u.onboardingStatus !== 'password_pending').length;

  if (userContextLoading) {
    return (
      <div className="min-h-screen flex flex-col" style={pageBackground}>
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-400"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col" style={pageBackground}>
      <main className="flex-1">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-8 text-white">Members & Onboarding</h1>

          <div
            className="rounded-lg mb-8 p-6 border border-white/10 shadow-2xl"
            style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-5">
              <div>
                <h2 className="text-xl font-semibold text-white">Users & Onboarding Timeline</h2>
                <p className="text-sm text-white/60 mt-1">
                  Track where users are in the onboarding funnel, identify drop-offs, and manage memberships.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <label className="text-sm text-white/70 flex items-center gap-2">
                  Stage:
                  <select
                    value={onboardingFilter}
                    onChange={(e) => setOnboardingFilter(e.target.value)}
                    className="bg-black/30 border border-white/20 text-white rounded px-2 py-1"
                  >
                    <option value="all">All Users</option>
                    <option value="password_pending">Password Pending (Drop-offs)</option>
                    <option value="completed">Completed Onboarding</option>
                  </select>
                </label>

                <label className="text-sm text-white/70 flex items-center gap-2">
                  Ending in:
                  <select
                    value={endingWithinDays}
                    onChange={(e) => setEndingWithinDays(parseInt(e.target.value, 10))}
                    className="bg-black/30 border border-white/20 text-white rounded px-2 py-1"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </label>
                <button
                  type="button"
                  onClick={fetchUsers}
                  className="px-3 py-2 rounded border border-white/20 text-white/90 hover:bg-white/10 text-sm"
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div className="rounded-md border border-white/15 px-4 py-3">
                <p className="text-white/60 text-sm">Total in View</p>
                <p className="text-white text-2xl font-semibold">{users.length}</p>
              </div>
              <div className="rounded-md border border-yellow-500/30 bg-yellow-500/5 px-4 py-3">
                <p className="text-yellow-300 text-sm">⚠️ Step 1 Done (Password Pending)</p>
                <p className="text-yellow-400 text-2xl font-semibold">{pendingCount}</p>
              </div>
              <div className="rounded-md border border-green-500/30 bg-green-500/5 px-4 py-3">
                <p className="text-green-300 text-sm">✓ Fully Completed</p>
                <p className="text-green-400 text-2xl font-semibold">{completedCount}</p>
              </div>
              <div className="rounded-md border border-white/15 px-4 py-3">
                <p className="text-white/60 text-sm">Active Subscriptions</p>
                <p className="text-white text-2xl font-semibold">{summary?.status.active || 0}</p>
              </div>
            </div>

            {actionMessage && (
              <div className="mb-4 rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-sm text-white/90">
                {actionMessage}
              </div>
            )}

            {error && (
              <div className="mb-4 rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-white/70 text-sm">Loading users...</div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-white/10">
                <table className="min-w-full text-sm">
                  <thead className="bg-white/5 text-white/70">
                    <tr>
                      <th className="text-left px-3 py-2">User</th>
                      <th className="text-left px-3 py-2">Onboarding Stage</th>
                      <th className="text-left px-3 py-2">Plan</th>
                      <th className="text-left px-3 py-2">Sub Status</th>
                      <th className="text-left px-3 py-2">Period End</th>
                      <th className="text-left px-3 py-2">Created</th>
                      <th className="text-left px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((record) => {
                        const isCurrentUser = record.id === String((user as any)?._id || (user as any)?.id || '');
                        const canDelete = !isCurrentUser && deletingUserId !== record.id;
                        const isPasswordPending = record.onboardingStatus === 'password_pending';

                        return (
                          <tr key={record.id} className="border-t border-white/10 text-white/90">
                            <td className="px-3 py-2">
                              <div className="font-medium">{record.email}</div>
                              <div className="text-xs text-white/60">
                                {[record.firstName, record.lastName].filter(Boolean).join(' ') || 'No name'}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              {isPasswordPending ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                                  🟡 Step 1 Paid (Password Pending)
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-300 border border-green-500/30">
                                  🟢 Fully Completed
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-semibold text-blue-400">
                                {record.subscription?.plan || 'none'}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                record.subscription?.status === 'active'
                                  ? 'bg-emerald-500/20 text-emerald-300'
                                  : 'bg-white/10 text-white/70'
                              }`}>
                                {record.subscription?.status || 'none'}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              {record.subscription?.currentPeriodEnd
                                ? new Date(record.subscription.currentPeriodEnd).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td className="px-3 py-2 text-white/60 text-xs">
                              {record.createdAt ? new Date(record.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-3 py-2 flex items-center gap-2">
                              {isPasswordPending && (
                                <button
                                  type="button"
                                  disabled={sendingReminderEmail === record.email}
                                  onClick={() => handleSendReminder(record.email)}
                                  className="px-2.5 py-1 rounded border border-yellow-400/40 bg-yellow-500/10 text-yellow-200 hover:bg-yellow-500/20 text-xs disabled:opacity-50"
                                >
                                  {sendingReminderEmail === record.email ? 'Sending...' : 'Resend Setup Link'}
                                </button>
                              )}

                              {isCurrentUser ? (
                                <span className="text-white/40 text-xs">Current user</span>
                              ) : (
                                <button
                                  type="button"
                                  disabled={!canDelete}
                                  onClick={() => handleDeleteUser(record)}
                                  className="px-2 py-1 rounded border border-red-300/40 text-red-200 hover:bg-red-500/10 text-xs disabled:opacity-50"
                                >
                                  {deletingUserId === record.id ? 'Deleting...' : 'Delete'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="px-3 py-3 text-white/60" colSpan={7}>
                          No users found for current filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminUsers;
